import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode,
} from "react"
import { QueryObserver, useQueryClient } from "@tanstack/react-query"

// Context Types
interface PhotoUploadContextValue {
  uploadProgress: Record<string, number>
  subscribeUploadProgress: (photoId: string) => () => void
  getUpdateUploadProgressFn: (photoId: string) => (percentage: number) => void
}

// Context
const PhotoUploadContext = createContext<PhotoUploadContextValue | null>(null)

// Provider Component
interface PhotoUploadProviderProps {
  children: ReactNode
}

export const getUploadProgressQueryKey = (photoId: string) => [
  "photoUploadProgress",
  photoId,
]

export const PhotoUploadProvider: React.FC<PhotoUploadProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  )

  const getUpdateUploadProgressFn = useCallback(
    (photoId: string) => (percentage: number) => {
      const queryKey = getUploadProgressQueryKey(photoId)
      if (percentage !== 100) {
        queryClient.setQueryData<number>(queryKey, percentage)
      } else {
        queryClient.removeQueries({
          queryKey,
        })
        setUploadProgress((prev) => {
          const { [photoId]: _, ...rest } = prev
          return rest
        })
      }
    },
    [queryClient],
  )

  const subscribeUploadProgress = useCallback(
    (photoId: string) => {
      const uploadProgressObserver = new QueryObserver<number>(queryClient, {
        queryKey: getUploadProgressQueryKey(photoId),
      })

      return uploadProgressObserver.subscribe(({ data }) => {
        if (typeof data === "number")
          setUploadProgress((prev) => ({ ...prev, [photoId]: data }))
      })
    },
    [queryClient],
  )

  const value: PhotoUploadContextValue = {
    uploadProgress,
    subscribeUploadProgress,
    getUpdateUploadProgressFn,
  }

  return (
    <PhotoUploadContext.Provider value={value}>
      {children}
    </PhotoUploadContext.Provider>
  )
}

// Hook to use the context
export const usePhotoUploadContext = () => {
  const context = useContext(PhotoUploadContext)
  if (!context) {
    throw new Error(
      "usePhotoUploadContext must be used within a PhotoUploadProvider",
    )
  }
  return context
}

// Hook to get upload progress for specific photos
export const useUploadProgress = (photoIds?: string[]) => {
  const { uploadProgress } = usePhotoUploadContext()

  if (!photoIds) return uploadProgress

  return photoIds.reduce(
    (acc, photoId) => {
      if (uploadProgress[photoId] !== undefined) {
        acc[photoId] = uploadProgress[photoId]
      }
      return acc
    },
    {} as Record<string, number>,
  )
}

// Hook to get upload progress for a single photo
export const usePhotoUploadProgress = (photoId?: string) => {
  const { uploadProgress } = usePhotoUploadContext()
  return photoId ? uploadProgress[photoId] : undefined
}
